import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useOutletContext } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

// import { isValidUrl } from '../../../utils/utils';

import {
  InputText,
  ErrorField,
  StyledSelect,
  StyledSwitch,
  InputDate,
  // Textarea,
} from '../../../lib/HooksFormFields';
import { parseHeader, formatIndicatorData } from '../../../utils/utils';
import { getAssociatedDbAction, getIndicatorsFieldsAction } from '../../../actions/indicators';

import styles from './data.module.scss';
import { postData } from '../../../actions';
import { INDICATORS_ASSOCIATED_DB, SET_TOAST } from '../../../actions/types';
import Loader from '../../../components/Loader';
import generateEntitiesData from '../../../utils/generateEntitiesData';
import generateAssociatedElementsData from '../../../utils/generateAssociatedElementsData';

const Data = () => {
  const {
    control, errors, watch, setValue, setError, submit,
  } = useOutletContext();
  const params = useParams();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const lang = i18n?.language || 'fr';
  const { dashboardReducer, indicatorReducer } = useSelector((store) => store);
  const { dashboard, user } = dashboardReducer;
  const { indicator, associatedDbs, requestLoading } = indicatorReducer;
  const today = new Date();
  const date = new Date(today?.setDate(today.getDate() - 1)).toISOString().split('T')[0];

  const currentForm = watch();
  const api = watch('api');
  const dataset = watch('dataset');
  const inheritance = watch('inheritance');
  const query = watch('query');
  const graph = watch('graph');
  const prevData = watch('data');

  function handleChangeSource(source) {
    if (source === 'associatedDb') {
      setValue('config.isAssociatedDb', true, { shouldDirty: true });
      setValue('config.isStat', false);
      setValue('config.startDate', '');
      setValue('config.endDate', '');
    } else if (source === 'stat') {
      setValue('config.isAssociatedDb', false, { shouldDirty: true });
      setValue('config.isStat', true);
      setValue('config.startDate', '2020-01-01');
      setValue('config.endDate', '');
      setValue('inheritance', false);
      setValue('api', null);
      setValue('dataset', null);
    } else {
      setValue('config.isAssociatedDb', false, { shouldDirty: true });
      setValue('config.isStat', false);
      setValue('config.startDate', '');
      setValue('config.endDate', '');
    }
  }

  async function handleChangeData(
    options,
    config,
    graphType,
    data,
  ) {
    const isAssociatedDb = !!config?.name?.value?.parent;
    let generatedData = null;
    const associatedConfig = {
      ...config,
      associatedConfig: {
        api: watch('api')?.value,
        key: watch('dataset')?.value,
      },
    };
    if (!isAssociatedDb) {
      generatedData = await generateEntitiesData(
        options,
        associatedConfig,
        graphType,
        data,
      );
    } else {
      generatedData = await generateAssociatedElementsData(
        options,
        associatedConfig,
        graphType,
        data,
      );
    }
    return generatedData;
  }

  /**
   * Called when clicking on button labelled 'update.indicator'
   *
   * @returns
   */
  async function handleChangeIndicator() {
    try {
      // PUT to /indicators/{id}
      await submit(); // we begin by SAVING the indicators (PUT route api/indicators/{id})

      const headers = {
        lang,
        key: dataset?.value,
        request: dataset?.api,
        header: parseHeader(indicator?.header),
        query: query ? encodeURI(query) : '',
        associatedId: currentForm?.config?.associatedDb?.value?.associatedDb,
        associatedDomainId: currentForm?.config?.associatedDb?.value?.associatedDomainId,
      };
      // POST to /craft/fields: retrieve the list of fields available to this indicator
      const fieldsData = await getIndicatorsFieldsAction(
        dispatch,
        headers,
      );

      const name = currentForm?.config?.params?.name;

      const field = fieldsData?.find(
        (f) => f.value === name,
      );

      // POST to /craft/request: Not sure: Create base request with the given settings?
      const res = await postData(INDICATORS_ASSOCIATED_DB, '/craft/request', null, {
        key: dataset?.value,
        requests: dataset?.api,
        lang,
        query: query ? encodeURI(query) : '',
        header: parseHeader(indicator?.header),
        associatedDomainId: field?.associatedDomainId,
        associatedId: field?.associatedId,
        startDate: currentForm?.config?.startDate,
        endDate: currentForm?.config?.endDate,
      });
      const { data } = res.data;
      console.log(name);
      const noDataFound = name !== 'all' && !name?.value?.includes('aggregation') && data?.entities?.filter((d) => (
        // eslint-disable-next-line no-prototype-builtins
        d.hasOwnProperty(name) ? d : null))?.length === 0;
      if (noDataFound) {
        return setError('noData', { message: `Aucune donnée trouvée "${name}"` });
      }

      // 'await' feels unnecessary here given this is not async
      // create a complex object containing the formatted data and more
      const options = await formatIndicatorData(data, fieldsData);
      console.debug('Created options in handleChangeIndicator', options);

      const updatedConfig = {
        ...currentForm?.config,
        associatedConfig: {
          api: dataset?.api,
          key: dataset?.value,
        },
      };
      // Transform result of
      // "postData(INDICATORS_ASSOCIATED_DB, '/craft/request'..."
      // into parsed data
      const generatedData = await handleChangeData(
        options,
        updatedConfig,
        graph,
        prevData,
      );

      if (generatedData.stacked) {
        setValue('config.legend.stacked', generatedData.stacked);
      }
      if (generatedData.data) {
        // This sets the value but is anything actually committed to DB?
        setValue('data', generatedData.data);
        return dispatch({
          type: SET_TOAST,
          payload: {
            type: 'success',
            message: "L'indicateur a été modifié",
          },
        });
      }
    } catch (err) {
      console.log(err);
      dispatch({
        type: SET_TOAST,
        payload: {
          type: 'error',
          message: err?.response?.data?.error || 'toaster.errors.500',
        },
      });
    }
    return null;
  }

  useEffect(() => {
    if (api?.value) {
      // console.debug('triggered useEffect [api]', api);
      setValue('requests', api.value);
    }
  }, [api]);

  useEffect(() => {
    if (dataset?.value) {
      // console.debug('triggered useEffect [dataset]', dataset);
      setValue('key', dataset.value);
      if (!currentForm?.config?.isStat) {
        const headers = {
          lang,
          key: dataset?.value,
          requests: dataset?.api,
          header: parseHeader(indicator?.header),
          query: query ? encodeURI(query) : '',
        };
        getAssociatedDbAction(dispatch, headers);
      }
    }
  }, [dataset]);

  // This is in opposition with what inheritanceSwitch is doing //
  // useEffect(() => {
  //   if (indicator?.key && indicator?.key !== dataset?.value) {
  //     console.debug('triggered useEffect [dataset?.value, indicator?.key]');
  //     setValue('inheritance', false);
  //     dispatch({
  //       type: INDICATORS_ASSOCIATED_DB,
  //       payload: null,
  //     });
  //   }
  // }, [dataset?.value, indicator?.key]);

  // function inheritanceSwitch(checked) {
  //   console.debug('inheritanceSwitch', checked);
  //   if (checked === true) {
  //     // empty the two selects & deactivate them
  //     console.debug('Clearing selects values');
  //     setValue('api', null);
  //     setValue('dataset', null);
  //     // setValue('key', null);
  //     // setValue('requests', null);
  //   }
  // }

  useEffect(() => {
    // console.debug('triggered useEffect [inheritance]', inheritance);
    if (inheritance === true) {
      // Find the dataset option object from user.keys that matches dashboard.key
      const datasetOption = user?.keys?.find((k) => k.value === dashboard?.key);
      // Find the api option object from user.apis or user.stats that matches dashboard.requests
      const apiOptions = currentForm?.config?.isStat ? user?.stats : user?.apis;
      const apiOption = apiOptions?.find((a) => a.value === dashboard?.requests);
      // Only replace the values if they exist in the dashboard
      if (datasetOption && apiOption) {
        // console.debug('Replacing dataset and api with parent values');
        setValue('dataset', datasetOption);
        setValue('api', apiOption);
      }
    }
  }, [inheritance]);

  return (
    <div className={styles.data}>
      {(user?.apis && ((params.indicatorId)
        || (dashboard && !params.indicatorId)) && !requestLoading
      ) ? (
        <>
          <div className={styles.row}>
            <div className={styles.sources}>
              <button
                className={`${!currentForm.config?.isAssociatedDb && !currentForm.config?.isStat ? styles.active : ''}`}
                onClick={() => handleChangeSource()}
              >
                {t('indicator.isEntities')}
              </button>
              <button
                className={`${currentForm.config?.isAssociatedDb && !currentForm.config?.isStat ? styles.active : ''}`}
                onClick={() => handleChangeSource('associatedDb')}
              >
                {t('indicator.isAssociatedDb')}
              </button>
              <button
                className={`${!currentForm.config?.isAssociatedDb
                  && currentForm?.config?.isStat ? styles.active : ''}`}
                onClick={() => handleChangeSource('stat')}
              >
                {t('indicator.isStats')}
              </button>
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.containerField}>
              <StyledSelect
                name="api"
                control={control}
                label={t('indicator.api')}
                disabled={currentForm.inheritance}
                isSearchable
                options={currentForm?.config?.isStat ? user?.stats : user?.apis}
              />

              {errors?.requests?.message && <ErrorField message={errors.requests.message} />}
            </div>
            <div className={styles.containerField}>
              <div>
                <StyledSelect
                  name="dataset"
                  control={control}
                  label={t('indicator.dataset')}
                  disabled={currentForm.inheritance}
                  isSearchable
                  options={user?.keys.filter((k) => k.api === api?.value) || []}
                />
              </div>
              {(dataset?.value !== indicator?.key
                || query !== indicator?.query
                || indicator?.config?.endDate !== currentForm?.config?.endDate
                || indicator?.config?.startDate !== currentForm?.config?.startDate)
                && indicator?.data
                && <div className={styles.update}>
                    <button
                    type='button'
                    onClick={() => handleChangeIndicator()}
                  >
                    {t('indicator.update')}
                  </button>
                  {errors?.noData?.message
                      && <ErrorField message={errors.noData.message} />}
                </div>
              }
              {errors?.key?.message && <ErrorField message={errors.key.message} />}
            </div>
          </div>
          {currentForm.config?.isAssociatedDb
            && <div className={styles.row}>
                <div className={styles.containerField}>
                <StyledSelect
                  name="config.associatedDb"
                  control={control}
                  label={t('indicator.associatedDb')}
                  isSearchable
                  options={associatedDbs}
                />
                {errors?.requests?.message && <ErrorField message={errors.requests.message} />}
              </div>
            </div>
          }
          <div className={`${styles.containerField} ${styles.switch}`}>
            <StyledSwitch
              name="inheritance"
              control={control}
              defaultValue={indicator?.inheritance}
            />
            <p>{t('indicator.inheritance')}</p>
          </div>
          {!currentForm?.config?.isStat
            && <div className={styles.containerField}>
              <div>
                <InputText
                  name="query"
                  control={control}
                  label={t('indicator.search')}
                  placeholder={'?criteria='}
                  rules={{}}
                />
              </div>
            </div>
          }
          {currentForm?.config?.isStat
            && <>
                <div className={styles.row}>
                  <div className={styles.containerField}>
                    <InputDate
                      name="config.startDate"
                      control={control}
                      label={t('indicator.startDate')}
                      maxDate={date}
                      placeholder={''}
                      rules={{
                        required: t('input.required'),
                      }}
                    />
                    {errors?.config?.startDate?.message
                    && <ErrorField message={errors?.config?.startDate?.message} />}
                </div>
                <div className={styles.containerField}>
                  <InputDate
                    name="config.endDate"
                    control={control}
                    maxDate={date}
                    label={t('indicator.endDate')}
                    placeholder={''}
                    isClearable={true}
                    defaultValue={''}
                  />
              </div>
              </div>
              {/* <div className={`${styles.row} ${styles.end}`}>
                <button
                  type='button'
                  onClick={async () => {
                    await setValue('config.endDate', null);
                    console.log(watch('config.endDate'));
                    await submit();
                    console.log('submited');
                  }}
                >
                  {t('indicator.currentDateUpdate')}
                </button>
              </div> */}
          </>
          }
        </>
        ) : (<div className={styles.loader}>
        <Loader />
      </div>)}
    </div>
  );
};

export default Data;
